import { Component, computed, inject } from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { CustomCapabilityConfigurationForm } from '../../../../../core/forms';
import { BookingConfigService } from '../../../../../core/services/booking-config.service';
import { Association, CalendarType, Calendar } from '../../../../../core/interfaces/config.interface';
import { KeyValuePair } from '@vendasta/ai-assistants';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';

const MEETING_URL = '../../../../events-and-meetings';
export const DEFAULT_MEETING_BOOKING_GOAL_ID = 'BookAppointmentsWithBookMeNow';

// These are calendars that list their meeting types/associations (15-minutes, 30-minutes, etc.)
// When executing an AI function, we prompt the AI to say "use this exact calendar for everything"
const calendarsTypesThatListAssociations: CalendarType[] = [CalendarType.PERSONAL, CalendarType.GROUP];

// These are calendars that contain types (also known as associations), but we expose the aggregate representation
// of the calendar, and we let the AI choose which type/association to use.
// For example, in an AI prompt we would say: "Look up all associations with this calendar and use the one you think is best"
const calendarsTypesThatAreAggregates: CalendarType[] = [CalendarType.SERVICE];

interface Selection {
  // The ID prefix can change, depending on the calendar type
  // If the CalendarType is of type personal or group, then the ID will be a meetingType (MT-)
  // If the CalendarType is of Service, then the ID will be the service menu ID (SVS-)
  id: string;
  calendarType: CalendarType;
}

@Component({
  selector: 'ai-book-me-now-capability-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatOptionModule,
    MatIconModule,
    TranslateModule,
    GalaxyFormFieldModule,
    AsyncPipe,
  ],
  templateUrl: './book-me-now-capability-form.component.html',
  styleUrl: './book-me-now-capability-form.component.scss',
})
export class BookMeNowCapabilityFormComponent extends CustomCapabilityConfigurationForm {
  private readonly bookingConfigService = inject(BookingConfigService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  protected readonly allCalendars$ = this.bookingConfigService.getAllCalendars();
  protected readonly calendarsTypesThatListAssociations$ = this.allCalendars$.pipe(
    map((calendars: Calendar[]) =>
      calendars.filter((calendar) => calendarsTypesThatListAssociations.includes(calendar.calendarType)),
    ),
  );
  protected readonly calendarsTypesThatAreAggregates$ = this.allCalendars$.pipe(
    map((calendars: Calendar[]) =>
      calendars.filter((calendar) => calendarsTypesThatAreAggregates.includes(calendar.calendarType)),
    ),
  );
  protected readonly serviceCalendars$ = this.calendarsTypesThatAreAggregates$.pipe(
    map((calendars: Calendar[]) => calendars.filter((calendar) => calendar.calendarType === CalendarType.SERVICE)),
    map((calendars: Calendar[]) => {
      return calendars.sort((a, b) => (a.name < b.name ? -1 : 1));
    }),
  );

  protected readonly calendarsWithAssociations = toSignal(this.calendarsTypesThatListAssociations$);
  protected readonly aggregateCalendars = toSignal(this.calendarsTypesThatAreAggregates$);

  protected readonly selectedCalendar = computed(() => {
    const configuration = this.configurationForm().value;

    if (!configuration || !Array.isArray(configuration)) {
      return null;
    }

    // First, check if the selection uses a meetingTypeId as its id
    const meetingTypeIdPair = configuration.find((pair) => pair.key === 'meetingTypeId');
    if (meetingTypeIdPair?.value) {
      const calendars = this.calendarsWithAssociations() ?? [];
      for (const calendar of calendars) {
        const matchingType = (calendar?.associations ?? []).find(
          (type) => type.meetingTypeId === meetingTypeIdPair.value,
        );
        if (matchingType) {
          return <Selection>{
            id: matchingType.meetingTypeId,
            calendarType: calendar.calendarType,
          };
        }
      }
    }

    // If a meetingTypeId was not found, or if an association was not found, then we will see if the selection was for an aggregate calendar
    const calendarIdPair = configuration.find((pair) => pair.key === 'calendarId');
    if (!calendarIdPair?.value) {
      return null;
    }
    const aggregateCalendars = this.aggregateCalendars() ?? [];
    const matchingCalendar = (aggregateCalendars ?? []).find(
      (calendar: Calendar) => calendar.id === calendarIdPair.value,
    );
    if (matchingCalendar) {
      return <Selection>{
        id: matchingCalendar.id,
        calendarType: matchingCalendar.calendarType,
      };
    }

    return null;
  });

  protected readonly compareCalendars = (option1: Selection | null, option2: Selection | null): boolean => {
    if (!option1 || !option2) return false;

    return option1.id === option2.id;
  };

  protected onSelection(event: { value: Selection | null }) {
    if (event.value) {
      this.configurationForm().setValue(this.getFormMutationsFromSelection(event.value));
    } else {
      this.configurationForm().setValue(null);
    }
  }

  protected getFormMutationsFromSelection(selection: Selection): KeyValuePair[] {
    let calendars = this.calendarsWithAssociations();

    if (!calendars || !calendars.length) {
      return [];
    }

    for (const calendar of calendars) {
      const matchingType = (calendar?.associations ?? []).find((type) => type.meetingTypeId === selection.id);
      if (matchingType) {
        return this.getKeyValuePairMutationsForCalendar(calendar, matchingType);
      }
    }

    calendars = this.aggregateCalendars() ?? [];
    for (const calendar of calendars) {
      if (calendar.id === selection.id) {
        return this.getKeyValuePairMutationsForCalendar(calendar);
      }
    }

    return [];
  }

  protected goToMeetings() {
    this.router.navigate([MEETING_URL], { relativeTo: this.route });
  }

  protected getKeyValuePairMutationsForCalendar(calendar: Calendar, associationSelected?: Association): KeyValuePair[] {
    switch (calendar.calendarType) {
      case CalendarType.PERSONAL:
      case CalendarType.GROUP:
        if (!associationSelected) {
          return [];
        }
        return [
          new KeyValuePair({ key: 'calendarId', value: associationSelected.calendarId }),
          new KeyValuePair({ key: 'meetingTypeId', value: associationSelected.meetingTypeId }),
        ];
      case CalendarType.SERVICE:
        return [
          new KeyValuePair({ key: 'calendarId', value: calendar.id }),
          new KeyValuePair({ key: 'slug', value: calendar.slug }),
        ];
    }

    return [];
  }
}
